import { booleanAttribute, Component, Input, OnInit } from '@angular/core';
import { JobAndUnitRequirementsDto, RequirementDto } from '../../models/job-unit-requirements.model';
import { BehaviorSubject, Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import {
    selectJobQualifications,
    selectJobQualificationsLoading,
    selectEffectiveAlternatives
} from '../job-qualifications/selectors/job-qualifications.selectors';
import { loadQualifications } from '../job-qualifications/actions/job-qualifications.actions';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MatButtonToggleChange, MatButtonToggleModule } from '@angular/material/button-toggle';
import { JobQualificationsMatchTypeEnum } from '../job-qualifications/enums/job-qualifications-match-type.enum';
import { LDFeatureManager } from '../../services/ld-feature-manager';
import { FeatureFlag } from '../../models/enums/feature-flag';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { LenientDatePipe } from 'src/app/shared/pipes/lenient-date.pipe';
import { NullDashPipe } from 'src/app/shared/pipes/null-dash.pipe';
import { StaffingRatioPipe } from 'src/app/shared/pipes/staffing-ratio.pipe';
import { JoinCommaPipe } from 'src/app/recruiting/jobs/details/components/tabs/job-details/job-section/pipes/join-comma.pipe';
import { TextFormatPipe } from 'src/app/shared/pipes/text-format.pipe';
import { TranslateExperiencePipe } from 'src/app/recruiting/jobs/details/components/tabs/job-details/job-section/pipes/translate-experience.pipe';
import { JobQualificationsCompareComponent } from 'src/app/shared/components/job-qualifications/job-qualifications-compare/job-qualifications-compare.component';
import { CommonModule } from '@angular/common';
import { ProfessionCodeEnum } from 'src/app/shared/models/enums/profession-code.enum';
import {
    JobAlternativeCertificationDto,
    CertificationWithRelated
} from 'src/app/shared/components/job-qualifications/models/certification-with-related.model';
import { JobQualificationsService } from 'src/app/shared/components/job-qualifications/job-qualifications.service';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { switchMap } from 'rxjs/operators';
import { getAlternativesForCertification } from 'src/app/shared/components/job-qualifications/utils/alternative-certifications.utils';

@UntilDestroy()
@Component({
    selector: 'nova-job-unit-requirements',
    templateUrl: './job-unit-requirements.component.html',
    styleUrls: ['./job-unit-requirements.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        MatCardModule,
        MatIconModule,
        MatButtonToggleModule,
        MatProgressSpinnerModule,
        JobQualificationsCompareComponent,
        LenientDatePipe,
        NullDashPipe,
        StaffingRatioPipe,
        TranslateExperiencePipe,
        JoinCommaPipe,
        TextFormatPipe
    ]
})
export class JobUnitRequirementsComponent implements OnInit {
    @Input({ transform: booleanAttribute })
    showUnitDetails: boolean;

    @Input({ transform: booleanAttribute })
    showJobRequirementsAndQualifications: boolean;

    @Input()
    data: JobAndUnitRequirementsDto;

    @Input() useClass: string;

    @Input() jobId: number;

    isLoading$: Observable<boolean>;

    currentToggleText = 'current';
    compareToggleText = 'compare';

    compareSelection$ = new BehaviorSubject<'current' | 'compare'>('current');
    matchType = JobQualificationsMatchTypeEnum;
    canReloadJobQualifications = false;

    showPreviousChargeExpOnlyForRN = false;
    isStateLicenseFormFieldEnabled = this._ldFeatureManager.isEnabledSignal(
        FeatureFlag.NovaJobDetailsStateLicenseFormField
    );

    effectiveAlternatives$: Observable<JobAlternativeCertificationDto[]>;
    effectiveAlternatives: JobAlternativeCertificationDto[] = [];
    availableRelatedCertifications: CertificationWithRelated[] = [];
    isAlternativeCertificationsEnabled = this._ldFeatureManager.isEnabledSignal(
        FeatureFlag.JobReqsAlternativeCertifications
    );

    get isRegisteredNurseProfession(): boolean {
        return this.data?.professionId === ProfessionCodeEnum.RN;
    }

    get shouldShowPreviousChargeExperience(): boolean {
        if (!this.showPreviousChargeExpOnlyForRN) {
            // Feature flag is off, show for all professions (existing behavior)
            return true;
        }
        // Feature flag is on, only show for RN
        return this.isRegisteredNurseProfession;
    }

    get shouldShowStateLicenseField(): boolean {
        return !!(
            this.isStateLicenseFormFieldEnabled() && this.data?.jobClinicalTrackRequirements?.activeLicense?.isRequired
        );
    }

    constructor(
        private readonly store: Store,
        private readonly _ldFeatureManager: LDFeatureManager,
        private readonly _jobQualificationsService: JobQualificationsService,
        private readonly _toasterService: ToasterService
    ) {}

    ngOnInit(): void {
        // Remove this block entirely when cleaning FF. It was added for the edge case where the FF status changes while the user is working with job details page.
        this._ldFeatureManager
            .isEnabled(FeatureFlag.ShowPreviousChargeExpOnlyForRN)
            .pipe(untilDestroyed(this))
            .subscribe((showPreviousChargeExpOnlyForRN) => {
                this.showPreviousChargeExpOnlyForRN = showPreviousChargeExpOnlyForRN;
                if (!this.canReloadJobQualifications) {
                    this.canReloadJobQualifications = true;
                } else {
                    this.store.dispatch(loadQualifications({ jobId: this.jobId }));
                }
            });

        this.data = null;
        this.isLoading$ = this.store.select(selectJobQualificationsLoading);
        this.effectiveAlternatives$ = this.store.select(selectEffectiveAlternatives);

        // Subscribe to effective alternatives from store (hierarchy-resolved from backend)
        this.effectiveAlternatives$.pipe(untilDestroyed(this)).subscribe((alternatives) => {
            this.effectiveAlternatives = alternatives || [];
        });

        // Load related certifications if feature flag is enabled
        this._ldFeatureManager
            .isEnabled(FeatureFlag.JobReqsAlternativeCertifications)
            .pipe(
                switchMap((isEnabled) => {
                    if (isEnabled) {
                        return this._jobQualificationsService.getAllCertificationsWithRelated();
                    }
                    return [];
                }),
                untilDestroyed(this)
            )
            .subscribe({
                next: (certifications) => {
                    this.availableRelatedCertifications = certifications || [];
                },
                error: (error) => {
                    this._toasterService.toast('Failed to load related certifications');
                    this.availableRelatedCertifications = [];
                }
            });

        this.store.dispatch(loadQualifications({ jobId: this.jobId }));

        this.store
            .select(selectJobQualifications)
            .pipe(untilDestroyed(this))
            .subscribe((data) => {
                this.data = data;
            });
    }

    get shouldDisplayComparison() {
        return (
            (this.data?.matchType === this.matchType.UnitRequirements ||
                this.data?.matchType === this.matchType.JobTrack ||
                this.data?.matchType === this.matchType.UnitTrack) &&
            this.data?.hasJobLevelEdits &&
            !this.data?.allChangesMatch
        );
    }

    get comparisonText() {
        const matchType = this.data?.matchType;
        switch (matchType) {
            case this.matchType.JobTrack:
                return 'Track Comp';
            case this.matchType.UnitTrack:
            case this.matchType.UnitRequirements:
                return 'UD Comp';
            default:
                return '';
        }
    }

    getUniqueSkillsCategories(data: RequirementDto[]) {
        const categories = data.filter((c) => !c.isNA).map((item) => item.category);
        return [...new Set(categories)];
    }

    getSkillsByCategory(data: RequirementDto[], category) {
        return data.filter((item) => item.category === category && !item.isNA);
    }

    onChangesToggled(toggleChange: MatButtonToggleChange) {
        this.compareSelection$.next(toggleChange.value);
    }

    getAlternativesForCertification(sourceCertificationId: number): string[] {
        if (!this.isAlternativeCertificationsEnabled()) {
            return [];
        }

        return getAlternativesForCertification(
            sourceCertificationId,
            this.effectiveAlternatives,
            this.availableRelatedCertifications
        );
    }
}
